/**
 * RunClassificationChip (NC-1288)
 *
 * Makes "is this a real training run or a test run?" visible in the portal.
 *
 * The classification arrives already normalised from the API — runs-list and
 * runs-details resolve it via api/shared/runClassification.ts (persisted marker
 * first, legacy `E2E-` RunId prefix as a read-time fallback). This component
 * deliberately does NOT re-implement the prefix rule: duplicating it in the
 * frontend is exactly the drift the shared module exists to prevent. An absent
 * value means 'produktiv' (issue AC: no undefined badge on legacy runs).
 *
 * `alwaysShow` controls the noise/transparency trade-off: in the runs list only
 * test runs are badged, so a test run stands out in a scannable table; on the
 * run detail page the classification is always stated, because that is where
 * explicitness matters and there is room for it.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { Science as ScienceIcon } from '@mui/icons-material';
import { RunClassification } from '../services/types';

interface RunClassificationChipProps {
  classification?: RunClassification;
  /** Render a chip for 'produktiv' too (detail pages). Default: test-only. */
  alwaysShow?: boolean;
  size?: 'small' | 'medium';
}

const TEST_TOOLTIP =
  'Testlauf — zaehlt als Test/E2E-Lauf, nicht als echte Schulung. Entweder ausdruecklich so angelegt oder an der E2E-RunId-Konvention erkannt.';
const PROD_TOOLTIP =
  'Produktiver Lauf — eine echte Schulung. Laeufe ohne ausdrueckliche Kennzeichnung gelten als produktiv.';

export const RunClassificationChip: React.FC<RunClassificationChipProps> = ({
  classification,
  alwaysShow = false,
  size = 'small',
}) => {
  const isTest = classification === 'test';

  if (!isTest && !alwaysShow) return null;

  return (
    <Tooltip title={isTest ? TEST_TOOLTIP : PROD_TOOLTIP}>
      <Chip
        label={isTest ? 'Test' : 'Produktiv'}
        color={isTest ? 'warning' : 'default'}
        size={size}
        variant={isTest ? 'filled' : 'outlined'}
        icon={isTest ? <ScienceIcon /> : undefined}
        data-testid={`run-classification-${isTest ? 'test' : 'produktiv'}`}
      />
    </Tooltip>
  );
};

export default RunClassificationChip;
